<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { SENTIMENT_STYLES } from 'dashboard/components-next/Conversation/sentiment';

const props = defineProps({
  stats: { type: Object, default: () => ({}) },
  tags: { type: Array, default: () => [] },
});

const { t, locale } = useI18n();

const number = value =>
  new Intl.NumberFormat(locale.value.replace('_', '-')).format(
    Number(value) || 0
  );

const processed = computed(() => Number(props.stats.processed) || 0);
const sentiment = computed(() => props.stats.sentiment || {});
const spam = computed(() => props.stats.spam || {});

const sentimentTotal = computed(
  () =>
    ['positive', 'neutral', 'negative'].reduce(
      (sum, key) => sum + (Number(sentiment.value[key]) || 0),
      0
    ) || 1
);

const percent = value =>
  Math.round(((Number(value) || 0) / sentimentTotal.value) * 100);

const sentimentRows = computed(() =>
  ['positive', 'neutral', 'negative'].map(key => ({
    key,
    ...SENTIMENT_STYLES[key],
    count: Number(sentiment.value[key]) || 0,
    percent: percent(sentiment.value[key]),
  }))
);

const spamTotal = computed(
  () => (Number(spam.value.marked) || 0) + (Number(spam.value.resolved) || 0)
);

const kpis = computed(() => [
  {
    key: 'processed',
    icon: 'i-ri-sparkling-2-line',
    iconClass: 'bg-n-iris-3 text-n-iris-11',
    value: number(processed.value),
    label: t('AI_MARKUP.STATS.PROCESSED'),
  },
  {
    key: 'negative',
    icon: 'i-lucide-frown',
    iconClass: 'bg-n-ruby-3 text-n-ruby-11',
    value: `${percent(sentiment.value.negative)}%`,
    label: t('AI_MARKUP.STATS.NEGATIVE_SHARE'),
  },
  {
    key: 'spam',
    icon: 'i-lucide-shield-alert',
    iconClass: 'bg-n-amber-3 text-n-amber-11',
    value: number(spamTotal.value),
    label: t('AI_MARKUP.STATS.SPAM_CAUGHT'),
  },
  {
    key: 'blocked',
    icon: 'i-lucide-ban',
    iconClass: 'bg-n-slate-3 text-n-slate-11',
    value: number(spam.value.blocked_contacts),
    label: t('AI_MARKUP.STATS.BLOCKED'),
  },
]);

const topTags = computed(() => {
  const list = (props.stats.top_tags || []).slice(0, 8);
  const max = Math.max(1, ...list.map(item => Number(item.count) || 0));
  return list.map(item => ({
    ...item,
    color: props.tags.find(tag => tag.label === item.label)?.color || '#64748B',
    width: Math.max(3, Math.round(((Number(item.count) || 0) / max) * 100)),
  }));
});
</script>

<template>
  <div class="flex flex-col gap-5">
    <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <div
        v-for="kpi in kpis"
        :key="kpi.key"
        class="flex flex-col gap-2 p-4 border rounded-xl border-n-weak bg-n-alpha-1"
      >
        <span
          class="grid rounded-lg size-8 place-items-center"
          :class="kpi.iconClass"
        >
          <span class="size-4" :class="kpi.icon" />
        </span>
        <span class="text-2xl font-semibold tabular-nums text-n-slate-12">
          {{ kpi.value }}
        </span>
        <span class="text-xs text-n-slate-11">{{ kpi.label }}</span>
      </div>
    </div>

    <div class="grid gap-6 lg:grid-cols-2">
      <div class="flex flex-col gap-3">
        <span class="text-sm font-semibold text-n-slate-12">
          {{ t('AI_MARKUP.STATS.BY_SENTIMENT') }}
        </span>
        <div
          v-for="row in sentimentRows"
          :key="row.key"
          class="flex flex-col gap-1"
        >
          <div class="flex items-center justify-between text-xs">
            <span
              class="inline-flex items-center gap-1.5"
              :class="row.textClass"
            >
              <span class="size-3.5" :class="row.icon" />
              {{ t(row.label) }}
            </span>
            <span class="tabular-nums text-n-slate-11">
              {{ number(row.count) }}
              <span class="text-n-slate-10">· {{ row.percent }}%</span>
            </span>
          </div>
          <div class="h-2 overflow-hidden rounded-full bg-n-alpha-2">
            <div
              class="h-full rounded-full"
              :class="row.barClass"
              :style="{ width: `${Math.max(row.percent, row.count ? 2 : 0)}%` }"
            />
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-3">
        <span class="text-sm font-semibold text-n-slate-12">
          {{ t('AI_MARKUP.STATS.TOP_TAGS') }}
        </span>
        <div v-if="topTags.length" class="flex flex-col gap-2">
          <div
            v-for="tag in topTags"
            :key="tag.label"
            class="grid items-center grid-cols-[8.5rem_1fr_3rem] gap-2 text-xs"
          >
            <span
              class="inline-flex items-center gap-1.5 truncate text-n-slate-12"
            >
              <span
                class="flex-shrink-0 rounded-full size-2"
                :style="{ background: tag.color }"
              />
              <span class="truncate">{{ tag.label }}</span>
            </span>
            <div class="h-2 overflow-hidden rounded-full bg-n-alpha-2">
              <div
                class="h-full rounded-full opacity-80"
                :style="{ width: `${tag.width}%`, background: tag.color }"
              />
            </div>
            <span class="tabular-nums text-end text-n-slate-11">{{
              number(tag.count)
            }}</span>
          </div>
        </div>
        <p v-else class="m-0 text-sm text-n-slate-10">
          {{ t('AI_MARKUP.STATS.NO_TAGS') }}
        </p>
      </div>
    </div>
  </div>
</template>
